// Attribution publicitaire côté navigateur.
//
// Le `fbclid` arrive dans l'URL au clic sur une publicité, mais la
// conversion n'est confirmée que bien plus tard, côté serveur, par le
// webhook Stripe. On le garde donc en localStorage le temps du parcours,
// et on le transmet UNE SEULE FOIS au moment de l'inscription : c'est le
// dernier instant où le client est réellement devant son navigateur.

const ATTRIB_KEY = 'ctrlurself.attrib.v1'
const CONSENT_KEY = 'ctrlurself.consent.v1'

// ===========================================================================
//  MODE TEST — piloté par REACT_APP_DEV_MODE dans .env (true / false).
//
//  Le pendant navigateur de APP_ENV côté backend (CTRL-BACKEND/.env). Il
//  n'est PAS redondant : REACT_APP_API_URL pointe aujourd'hui sur
//  api.selfcntrl.com, donc un `npm start` en local envoie ses balises au
//  backend DE PRODUCTION, qui lui n'est pas en mode test et notifie pour de
//  vrai. Il faut donc aussi se taire à la source.
//
//  Ce qui est coupé : le pixel Meta (PageView, InitiateCheckout…) et les
//  balises d'activité vers /track/event.php (voir notify.js). Ce qui ne
//  l'est pas : le reste de l'application, qui fonctionne normalement.
//
//  ⚠️ Create React App ne relit .env qu'au démarrage : après modification,
//  couper `npm start` (Ctrl+C) et relancer.
//
//  ⚠️ La valeur est FIGÉE DANS LE BUNDLE au moment du `npm run build`.
//  Builder avec REACT_APP_DEV_MODE=true livrerait un site en ligne qui ne
//  mesure plus rien. La remettre à false avant tout build de production.
// ===========================================================================
export const DEV_MODE = process.env.REACT_APP_DEV_MODE === 'true'

/** Trace en console ce qui aurait été envoyé, pour que le mode test se voie. */
function devSkip (what, detail) {
  // eslint-disable-next-line no-console
  console.info('[mode test] non envoyé —', what, detail ?? '')
}

// ===========================================================================
//  ⚠️ LA SEULE LIGNE À CHANGER POUR ÊTRE CONFORME EN FRANCE.
//
//  `true`  = le suivi part par défaut, sans attendre de réponse au bandeau.
//            C'est le réglage voulu pendant le développement : on ne veut pas
//            cliquer « Accepter » à chaque test pour que le pixel tire.
//  `false` = rien ne part avant un accord explicite. C'est ce qu'exige la
//            CNIL, et c'est cohérent avec la promesse « personne ne le
//            saura » que la landing fait par ailleurs.
//
//  Le bouton « Refuser » du bandeau fonctionne dans les DEUX cas : il écrit
//  'denied' et tout se coupe réellement. Il n'y a donc rien d'autre à
//  toucher ici le jour du déploiement.
// ===========================================================================
const CONSENT_DEFAULT = true

export function trackingAllowed () {
  try {
    const v = localStorage.getItem(CONSENT_KEY)
    if (v === 'granted') return true
    if (v === 'denied') return false
    return CONSENT_DEFAULT
  } catch { return false }
}

/** Vrai si le visiteur a déjà répondu au bandeau — donc s'il faut le taire. */
export function hasChosen () {
  try {
    const v = localStorage.getItem(CONSENT_KEY)
    return v === 'granted' || v === 'denied'
  } catch {
    // Stockage refusé : on ne peut pas mémoriser la réponse, donc réafficher
    // le bandeau à chaque page serait insupportable. On le tait.
    return true
  }
}

/** Appelé par le bandeau. Un refus efface ce qui a été collecté. */
export function setConsent (granted) {
  try {
    localStorage.setItem(CONSENT_KEY, granted ? 'granted' : 'denied')
    if (!granted) localStorage.removeItem(ATTRIB_KEY)
  } catch { /* stockage indisponible */ }
}

function read () {
  try { return JSON.parse(localStorage.getItem(ATTRIB_KEY) || '{}') }
  catch { return {} }
}

function write (data) {
  try { localStorage.setItem(ATTRIB_KEY, JSON.stringify(data)) }
  catch { /* quota plein ou navigation privée */ }
}

/** Lit un cookie posé par le pixel (_fbp, _fbc). */
function cookie (name) {
  const m = document.cookie.match(new RegExp('(^|;\\s*)' + name + '=([^;]*)'))
  return m ? decodeURIComponent(m[2]) : null
}

/**
 * À appeler au chargement de la landing.
 * Meta attend le format `fb.1.<horodatage ms>.<fbclid>`. On écrase la
 * valeur existante à chaque nouveau clic : c'est la publicité la plus
 * récente qui doit recevoir le crédit, pas la première.
 */
export function captureAttribution () {
  if (!trackingAllowed()) return
  const fbclid = new URLSearchParams(window.location.search).get('fbclid')
  if (!fbclid) return
  write({ ...read(), fbc: `fb.1.${Date.now()}.${fbclid}`, at: Date.now() })
}

/**
 * Les signaux à joindre à l'inscription. Le cookie posé par le pixel prime
 * s'il existe : il est mieux formé que notre reconstruction.
 */
export function getAttribution () {
  if (!trackingAllowed()) return {}
  const stored = read()
  return {
    fbc: cookie('_fbc') || stored.fbc || undefined,
    fbp: cookie('_fbp') || undefined
  }
}

// --- Pixel navigateur ------------------------------------------------------
let pixelReady = false

/** Charge le pixel Meta. Sans identifiant configuré, ne fait rien. */
export function initPixel () {
  const id = process.env.REACT_APP_FB_PIXEL_ID
  if (!id || pixelReady || !trackingAllowed()) return
  // Le pixel n'est même pas chargé en mode test : sans `window.fbq`, tout ce
  // qui suit (track, trackPageView, setUserId) se tait tout seul.
  if (DEV_MODE) { devSkip('pixel Meta', id); return }
  if (typeof window === 'undefined') return

  /* eslint-disable */
  !function (f, b, e, v, n, t, s) {
    if (f.fbq) return; n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments)
    }
    if (!f._fbq) f._fbq = n; n.push = n; n.loaded = !0; n.version = '2.0'; n.queue = []
    t = b.createElement(e); t.async = !0; t.src = v
    s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s)
  }(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js')
  /* eslint-enable */

  window.fbq('init', id)
  window.fbq('track', 'PageView')
  pixelReady = true
}

/**
 * Rattache notre identifiant d'utilisateur au pixel (« external_id »).
 *
 * C'est le seul signal de rapprochement qui survit à un changement
 * d'appareil ou à la perte des cookies. Le serveur envoie déjà le même
 * identifiant avec le Purchase (`middleware/meta.php`) : les deux doivent
 * arriver identiques pour que Meta les rapproche.
 *
 * Deux choses à savoir avant de toucher à ça :
 *   - le rapprochement avancé se déclare à l'INIT, pas dans l'événement ;
 *     appeler `init` une seconde fois avec le même pixel met à jour les
 *     paramètres, c'est la méthode documentée ;
 *   - on envoie l'identifiant EN CLAIR et c'est Meta qui le normalise puis
 *     le hache en SHA-256, exactement comme `meta_hash()` côté serveur. Un
 *     numéro n'a ni casse ni espaces, les deux hachages concordent donc.
 *     Si un jour le taux de correspondance de l'external_id reste à zéro
 *     dans les diagnostics, c'est cette hypothèse qu'il faut vérifier en
 *     premier.
 */
export function setUserId (id) {
  const pixel = process.env.REACT_APP_FB_PIXEL_ID
  if (!pixel || !id || !trackingAllowed()) return
  if (typeof window === 'undefined' || !window.fbq) return
  window.fbq('init', pixel, { external_id: String(id) })
}

/**
 * Événement de haut de tunnel. Le Purchase n'est JAMAIS envoyé d'ici : il
 * part du serveur, où aucun bloqueur de publicité ne peut l'intercepter.
 *
 * `eventId` sert à la déduplication quand le serveur envoie LE MÊME
 * événement de son côté (c'est le cas de CompleteRegistration) : Meta
 * rapproche les deux et n'en compte qu'un. Sans lui, une inscription
 * compterait double et le coût par acquisition affiché serait divisé par
 * deux — l'algorithme apprendrait sur un chiffre faux.
 */
export function track (event, params = {}, eventId) {
  if (DEV_MODE) { devSkip('événement', event); return }
  if (!trackingAllowed() || typeof window === 'undefined' || !window.fbq) return
  if (eventId) window.fbq('track', event, params, { eventID: eventId })
  else window.fbq('track', event, params)
}

/**
 * PageView sur changement de route.
 *
 * Indispensable ici : la landing est une application à page unique, donc le
 * PageView d'`index.js` ne part QU'UNE FOIS, au démarrage. Passer de `/` à
 * `/register` avec React Router n'envoie rien — Meta ne voit jamais que le
 * formulaire a été atteint, et il devient impossible de recibler « les gens
 * qui ont vu la page d'inscription ».
 */
export function trackPageView () {
  if (!trackingAllowed() || typeof window === 'undefined' || !window.fbq) return
  window.fbq('track', 'PageView')
}
